import { useEffect, useRef } from 'react';
import { Link, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useSession } from './store/session.js';
import { useI18n, useT, LOCALES, LOCALE_NAMES, type Locale } from './i18n/index.js';
import { api } from './api/client.js';
import { BrandMark, IconCart, IconGrid, IconUser } from './components/icons.js';
import { Spinner } from './components/ui.js';
import { CatalogPage } from './routes/CatalogPage.js';
import { ProductPage } from './routes/ProductPage.js';
import { EditorPage } from './routes/EditorPage.js';
import { CheckoutPage } from './routes/CheckoutPage.js';
import { MisDisenosPage } from './routes/MisDisenosPage.js';
import { CompartidoPage } from './routes/CompartidoPage.js';
import { OrdersPage } from './routes/OrdersPage.js';
import { OrderPage } from './routes/OrderPage.js';
import { AccountPage } from './routes/AccountPage.js';
import { LoginPage } from './routes/LoginPage.js';
import { AdminPage } from './admin/AdminPage.js';

export function App() {
  const { loading, load } = useSession();
  const isStaff = useSession((s) => s.isStaff());
  const location = useLocation();

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) return <Spinner />;

  // El editor ocupa toda la pantalla: la barra superior le robaria espacio
  // vertical, que en movil es justo lo que falta.
  const fullscreen = location.pathname.startsWith('/editor/');

  return (
    <div className="flex min-h-full flex-col bg-papel">
      {!fullscreen && <TopBar />}
      <main className="flex-1">
        <Routes>
          <Route path="/" element={<CatalogPage />} />
          <Route path="/productos/:id" element={<ProductPage />} />
          <Route path="/editor/:designId" element={<EditorPage />} />
          <Route path="/checkout/:designId" element={<CheckoutPage />} />
          <Route path="/mis-disenos" element={<MisDisenosPage />} />
          {/* El enlace compartido NO pide sesion: quien lo recibe no es el cliente. */}
          <Route path="/ver/:token" element={<CompartidoPage />} />
          <Route path="/mis-pedidos" element={<OrdersPage />} />
          <Route path="/pedidos/:id" element={<OrderPage />} />
          <Route path="/cuenta" element={<AccountPage />} />
          <Route path="/entrar" element={<LoginPage />} />
          <Route path="/admin/*" element={isStaff ? <AdminPage /> : <Navigate to="/entrar?next=/admin" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      {!fullscreen && <BottomNav />}
    </div>
  );
}

function TopBar() {
  const t = useT();
  const { user, login, logout, isStaff, ssoEnabled } = useSession();
  const locale = useI18n((s) => s.locale);
  const setLocale = useI18n((s) => s.setLocale);
  const location = useLocation();

  const changeLocale = (next: Locale) => {
    setLocale(next);
    // Se guarda en el servidor para que el correo y los avisos salgan en el
    // mismo idioma que la pantalla.
    if (user) void api.setLocale(next).catch(() => undefined);
  };

  return (
    <header className="safe-top sticky top-0 z-30 border-b border-borde bg-white/95 backdrop-blur">
      <div className="mx-auto flex max-w-6xl items-center gap-3 px-4 py-3">
        <Link to="/" className="flex items-center gap-2 font-semibold">
          <BrandMark size={26} className="text-tinta" />
          <span className="hidden sm:inline">{t('app.brand')}</span>
          <span className="text-tinta-suave">{t('app.name')}</span>
        </Link>

        <nav className="ml-auto hidden items-center gap-1 lg:flex">
          <Link to="/" className="toque rounded-lg px-3 py-2 text-sm hover:bg-papel-suave">
            {t('nav.catalog')}
          </Link>
          {user && (
            <Link to="/mis-pedidos" className="toque rounded-lg px-3 py-2 text-sm hover:bg-papel-suave">
              {t('nav.myOrders')}
            </Link>
          )}
          {isStaff() && (
            <Link to="/admin" className="toque rounded-lg px-3 py-2 text-sm hover:bg-papel-suave">
              {t('nav.admin')}
            </Link>
          )}
        </nav>

        <label className="ml-auto lg:ml-0">
          <span className="sr-only">Idioma</span>
          <select
            value={locale}
            onChange={(e) => changeLocale(e.target.value as Locale)}
            className="toque rounded-lg border border-borde bg-white px-2 py-1.5 text-sm"
          >
            {LOCALES.map((l) => (
              <option key={l} value={l}>
                {LOCALE_NAMES[l]}
              </option>
            ))}
          </select>
        </label>

        {user ? (
          <div className="flex items-center gap-2">
            <Link to="/cuenta" className="toque hidden items-center gap-2 rounded-lg px-3 py-2 text-sm sm:flex">
              <IconUser size={18} />
              <span className="max-w-32 truncate">{user.name}</span>
            </Link>
            <button onClick={() => void logout()} className="toque rounded-lg px-3 py-2 text-sm hover:bg-papel-suave">
              {t('nav.logout')}
            </button>
          </div>
        ) : (
          <button
            data-test="entrar"
            onClick={() => {
              void login(location.pathname).catch(() => {
                // Sin SSO configurado (desarrollo), se ofrece el acceso local.
                window.location.href = `/entrar?next=${encodeURIComponent(location.pathname)}`;
              });
            }}
            className="toque rounded-lg bg-marca px-4 py-2 text-sm font-medium text-white"
          >
            {/* Donde no hay SSO no se promete «con Besbello»: se dice «Entrar». */}
            {ssoEnabled ? t('nav.login') : t('nav.loginLocal')}
          </button>
        )}
      </div>
    </header>
  );
}

/** Navegacion inferior por pulgar: en movil, arriba no se llega. */
function BottomNav() {
  const t = useT();
  const user = useSession((s) => s.user);
  const location = useLocation();
  const nav = useRef<HTMLElement>(null);

  /**
   * La navegacion publica SU PROPIA ALTURA como variable CSS.
   *
   * Cualquier pantalla que ancle algo al fondo (la barra de "Personalizar" de la
   * ficha, por ejemplo) tiene que apoyarse encima de esta barra, no debajo: en
   * movil la navegacion se comia el toque y el cliente pulsaba "Catalogo"
   * creyendo que pulsaba el boton.
   *
   * Se MIDE en vez de escribir un numero: asi no se descuadra si manana cambia
   * el tamano del icono, el texto o el area segura del telefono. En pantalla
   * grande la barra esta oculta y la variable vale 0 sola.
   */
  useEffect(() => {
    const elemento = nav.current;
    if (!elemento) return;
    const publicar = () => {
      const alto = elemento.getBoundingClientRect().height;
      document.documentElement.style.setProperty('--alto-nav-inferior', `${Math.round(alto)}px`);
    };
    publicar();
    const observer = new ResizeObserver(publicar);
    observer.observe(elemento);
    window.addEventListener('resize', publicar);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', publicar);
      document.documentElement.style.setProperty('--alto-nav-inferior', '0px');
    };
  }, []);

  const items = [
    { to: '/', label: t('nav.catalog'), icon: <IconGrid size={22} /> },
    ...(user ? [{ to: '/mis-pedidos', label: t('nav.myOrders'), icon: <IconCart size={22} /> }] : []),
    ...(user ? [{ to: '/cuenta', label: t('nav.account'), icon: <IconUser size={22} /> }] : []),
  ];

  return (
    <nav
      ref={nav}
      className="safe-bottom sticky bottom-0 z-30 border-t border-borde bg-white lg:hidden"
    >
      <ul className="mx-auto flex max-w-6xl">
        {items.map((item) => {
          const active = location.pathname === item.to;
          return (
            <li key={item.to} className="flex-1">
              <Link
                to={item.to}
                className={`toque flex flex-col items-center gap-0.5 py-2 text-xs ${
                  active ? 'text-marca font-medium' : 'text-tinta-suave'
                }`}
                aria-current={active ? 'page' : undefined}
              >
                {item.icon}
                {item.label}
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
